import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react'
import { Select as SelectPrimitive } from 'radix-ui'
import * as React from 'react'

import { cn } from '@/lib/utils'
import {
    inputVariants,
    linhaControleClass,
    rotuloCampoClass,
    rotuloGrandeClass,
} from './input'

function Select({
    ...props
}: React.ComponentProps<typeof SelectPrimitive.Root>) {
    return <SelectPrimitive.Root data-slot="select" {...props} />
}

function SelectGroup({
    ...props
}: React.ComponentProps<typeof SelectPrimitive.Group>) {
    return <SelectPrimitive.Group data-slot="select-group" {...props} />
}

function SelectValue({
    ...props
}: React.ComponentProps<typeof SelectPrimitive.Value>) {
    return <SelectPrimitive.Value data-slot="select-value" {...props} />
}

/**
 * Gatilho no formato do campo `filled` do MUI: com `label`, o rótulo de 12px
 * fica dentro da caixa e o valor de 16px embaixo. A seta é absoluta e centrada
 * verticalmente — como o `.MuiSelect-icon` — para não descer junto com o valor
 * quando a caixa está empilhada.
 */
function SelectTrigger({
    className,
    variant,
    size,
    label,
    rotuloGrande,
    children,
    ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> & {
    variant?: 'filled' | 'outlined'
    size?: 'small' | 'medium'
    label?: React.ReactNode
    /**
     * Rótulo no lugar do valor. Quem sabe se há seleção, placeholder ou lista
     * aberta é quem monta o `Select` — aqui só se aplica o estado.
     */
    rotuloGrande?: boolean
}) {
    const ariaInvalid = props['aria-invalid']
    const invalid =
        ariaInvalid !== undefined &&
        ariaInvalid !== false &&
        ariaInvalid !== 'false'
    const temRotulo = label !== undefined && label !== ''

    return (
        <SelectPrimitive.Trigger
            data-slot="select-trigger"
            data-invalid={invalid || undefined}
            className={cn(
                inputVariants({ variant, size, comRotulo: temRotulo }),
                'cursor-pointer pr-8',
                'font-sans text-left whitespace-nowrap',
                'disabled:cursor-not-allowed',
                '[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*=size-])]:size-4',
                // Valor mais largo que a caixa sai com reticências, como no
                // `MuiSelect` — sem isto ele vaza e empurra a coluna do grid.
                // `block`, e não `flex`: `text-overflow` não vale para item de
                // flex, o corte sairia no meio da letra e sem reticências.
                '[&_[data-slot=select-value]]:block',
                '[&_[data-slot=select-value]]:min-w-0',
                '[&_[data-slot=select-value]]:truncate',
                className,
            )}
            {...props}
        >
            {temRotulo && (
                <span
                    data-slot="select-trigger-label"
                    className={cn(
                        rotuloCampoClass,
                        rotuloGrande && rotuloGrandeClass,
                    )}
                >
                    {label}
                </span>
            )}
            <span className={linhaControleClass}>{children}</span>
            <SelectPrimitive.Icon asChild>
                <ChevronDownIcon className="absolute top-1/2 right-3 size-4 -translate-y-1/2 opacity-50" />
            </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
    )
}

function SelectContent({
    className,
    children,
    position = 'popper',
    ...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) {
    return (
        <SelectPrimitive.Portal>
            <SelectPrimitive.Content
                data-slot="select-content"
                position={position}
                className={cn(
                    'z-(--z-flutuante) max-h-(--radix-select-content-available-height) min-w-[8rem]',
                    'origin-(--radix-select-content-transform-origin)',
                    'relative overflow-x-hidden overflow-y-auto',
                    'rounded-md border bg-card p-1 text-card-foreground shadow-md',
                    'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
                    'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
                    'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
                    'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
                    position === 'popper' &&
                        'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
                    className,
                )}
                {...props}
            >
                <SelectScrollUpButton />
                <SelectPrimitive.Viewport
                    className={cn(
                        'p-1',
                        position === 'popper' &&
                            'h-(--radix-select-trigger-height) w-full min-w-(--radix-select-trigger-width) scroll-my-1',
                    )}
                >
                    {children}
                </SelectPrimitive.Viewport>
                <SelectScrollDownButton />
            </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
    )
}

function SelectLabel({
    className,
    ...props
}: React.ComponentProps<typeof SelectPrimitive.Label>) {
    return (
        <SelectPrimitive.Label
            data-slot="select-label"
            className={cn(
                'px-2 py-1.5 text-xs text-muted-foreground',
                className,
            )}
            {...props}
        />
    )
}

function SelectItem({
    className,
    children,
    ...props
}: React.ComponentProps<typeof SelectPrimitive.Item>) {
    return (
        <SelectPrimitive.Item
            data-slot="select-item"
            className={cn(
                'relative flex w-full cursor-pointer items-center gap-2',
                'rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none',
                'focus:bg-accent',
                'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
                '[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*=size-])]:size-4',
                className,
            )}
            {...props}
        >
            <span className="absolute right-2 flex size-3.5 items-center justify-center">
                <SelectPrimitive.ItemIndicator>
                    <CheckIcon className="size-4" />
                </SelectPrimitive.ItemIndicator>
            </span>
            <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
        </SelectPrimitive.Item>
    )
}

function SelectSeparator({
    className,
    ...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) {
    return (
        <SelectPrimitive.Separator
            data-slot="select-separator"
            className={cn(
                'pointer-events-none -mx-1 my-1 h-px bg-border',
                className,
            )}
            {...props}
        />
    )
}

function SelectScrollUpButton({
    className,
    ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
    return (
        <SelectPrimitive.ScrollUpButton
            data-slot="select-scroll-up-button"
            className={cn(
                'flex cursor-default items-center justify-center py-1',
                className,
            )}
            {...props}
        >
            <ChevronUpIcon className="size-4" />
        </SelectPrimitive.ScrollUpButton>
    )
}

function SelectScrollDownButton({
    className,
    ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
    return (
        <SelectPrimitive.ScrollDownButton
            data-slot="select-scroll-down-button"
            className={cn(
                'flex cursor-default items-center justify-center py-1',
                className,
            )}
            {...props}
        >
            <ChevronDownIcon className="size-4" />
        </SelectPrimitive.ScrollDownButton>
    )
}

export {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectScrollDownButton,
    SelectScrollUpButton,
    SelectSeparator,
    SelectTrigger,
    SelectValue,
}
